import {
    SHOP_BLOCK_LOCALIZATION_SCHEMA_DATA,
    SHOP_BLOCK_LOCALIZATION_SCHEMA_VERSION,
} from "./generated/shopBlockLocalizationSchema.js";
import { isShopStorefrontMessageKey } from "./storefrontCatalog";
import type { ShopStorefrontMessageKey } from "./storefrontCatalog";
import {
    isShopDynamicEntityField,
    SHOP_DYNAMIC_ENTITY_FIELD_OPTION,
} from "./shopBlocks";
import type { ShopRichTextSegment, ShopTemplateBlock } from "./types";

export type ShopBlockLocalizationFormat = "plainText" | "richText";

export type ShopBlockLocalizationFieldDescriptor = Readonly<{
    defaultMessageKey?: ShopStorefrontMessageKey;
    fieldCode: string;
    format: ShopBlockLocalizationFormat;
    localizable: boolean;
    multiline: boolean;
}>;

export type ShopBlockLocalizationSchema = Readonly<Record<string, readonly ShopBlockLocalizationFieldDescriptor[]>>;
export type ShopBlockLocalizedValue = string | readonly ShopRichTextSegment[];

export const SHOP_BLOCK_LOCALIZATION_SCHEMA = SHOP_BLOCK_LOCALIZATION_SCHEMA_DATA satisfies ShopBlockLocalizationSchema;
export { SHOP_BLOCK_LOCALIZATION_SCHEMA_VERSION };

export function getShopBlockLocalizationFields(blockType: string): readonly ShopBlockLocalizationFieldDescriptor[] {
    return SHOP_BLOCK_LOCALIZATION_SCHEMA[blockType as keyof typeof SHOP_BLOCK_LOCALIZATION_SCHEMA] ?? [];
}

export function getShopBlockLocalizationField(
    blockType: string,
    fieldCode: string,
): ShopBlockLocalizationFieldDescriptor | null {
    return getShopBlockLocalizationFields(blockType).find((field) => field.fieldCode === fieldCode) ?? null;
}

export function getShopBlockDefaultMessageKey(
    block: ShopTemplateBlock,
    fieldCode: string,
): ShopStorefrontMessageKey | null {
    const override = block.defaultMessageKeys?.[fieldCode];

    if (typeof override === "string" && isShopStorefrontMessageKey(override)) {
        return override;
    }

    return getShopBlockLocalizationField(block.type, fieldCode)?.defaultMessageKey ?? null;
}

export function isShopBlockFieldLocalizable(blockType: string, fieldCode: string): boolean {
    return getShopBlockLocalizationField(blockType, fieldCode)?.localizable === true;
}

export function getShopBlockLocalizedValue(
    block: ShopTemplateBlock,
    fieldCode: string,
): ShopBlockLocalizedValue | null {
    if (fieldCode === "content.text" && shopBlockTextComesFromEntity(block)) {
        return null;
    }

    const descriptor = getShopBlockLocalizationField(block.type, fieldCode);

    if (!descriptor?.localizable || !fieldCode.startsWith("content.")) {
        return null;
    }

    const key = fieldCode.slice("content.".length);
    const richTextValue = block.richText?.[key];
    const value = block.content?.[key];

    if (
        descriptor.format === "richText"
        && Array.isArray(richTextValue)
        && typeof value === "string"
        && richTextValue.map((segment) => segment.text).join("") === value
    ) {
        return richTextValue;
    }

    return typeof value === "string" ? value : null;
}

/** Fields visibly owned by this block instance in the current document model. */
export function getShopBlockTranslationSurfaceFields(
    block: ShopTemplateBlock,
): readonly ShopBlockLocalizationFieldDescriptor[] {
    let fields = getShopBlockLocalizationFields(block.type).filter((field) => field.localizable);

    if (shopBlockTextComesFromEntity(block) || shopBlockTextComesFromRuntime(block)) {
        fields = fields.filter((field) => field.fieldCode !== "content.text");
    }

    if (block.type === "heading") {
        if (block.options?.documentRole === "title") {
            return fields.filter((field) => field.fieldCode === "content.text");
        }

        return fields.filter((field) => field.fieldCode !== "content.title");
    }

    if (block.type === "breadcrumb") {
        return fields.filter((field) => field.fieldCode === "content.homeLabel");
    }

    return fields;
}

function shopBlockTextComesFromEntity(block: ShopTemplateBlock): boolean {
    return (block.type === "heading" || block.type === "paragraph" || block.type === "text")
        && isShopDynamicEntityField(block.options?.[SHOP_DYNAMIC_ENTITY_FIELD_OPTION]);
}

function shopBlockTextComesFromRuntime(block: ShopTemplateBlock): boolean {
    const runtimeText = block.options?.runtimeText;

    return (block.type === "heading" || block.type === "paragraph" || block.type === "text")
        && (typeof runtimeText === "string" || typeof runtimeText === "number")
        && String(runtimeText) !== "";
}

export function shopBlockTranslationCode(
    scope: "global" | "page",
    ownerUuid: string,
    blockUuid: string,
    fieldCode: string,
): string {
    return `${scope}:${ownerUuid}:${blockUuid}:${fieldCode}`;
}
